import React from "react";
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { FaArrowLeft, FaArrowRight, FaTrashAlt, FaUserEdit } from "react-icons/fa";
import moment from 'moment';
import { Link } from 'react-router-dom';
import PortalPagination from '../../components/PortalPagination';
import PortalSpinner from '../../components/Spinner';
import { partnersService } from '../../services/partners';
import { patientsService } from '../../services/patients';
import { scheduleService } from '../../services/schedule';
import { canManagePatients, canManageSchedule } from '../../utils/access';
import { getErrorMessage } from '../../utils/errorMessage';
import { filterNamedPartners, filterNamedPatients } from '../../utils/records';

const acesso = Number(sessionStorage.getItem("tipoAcesso") || 0);

class Schedule extends React.Component {
    state = {
        datatable: [],
        show: false,
        message: '',
        modalShow: false,
        loading: true,
        saving: false,
        loadingPatients: false,
        entrada: '',
        saida: '',
        dataAgendamento: '',
        idParceiro: '',
        idPaciente: '',
        partnerList: [],
        patientList: [],
        allPatientList: [],
        id: '',
        currentPage: 1,
        postPerPage: 8
    };

    componentDidMount() {
        this.fetch();
    }

    componentWillUnmount() {
        clearTimeout(this.messageTimer);
    }

    flashMessage = (message) => {
        clearTimeout(this.messageTimer);
        this.setState({ show: true, message });
        this.messageTimer = setTimeout(() => this.setState({ show: false }), 7000);
    };

    showError = (error, fallbackMessage) => {
        this.flashMessage(getErrorMessage(error, fallbackMessage));
    };

    fetch = async () => {
        if (acesso < 1) {
            window.open('/', "_self");
            return;
        }

        try {
            this.setState({ loading: true });
            const currentDay = moment().format('YYYYMMDD');
            const patientRequest = canManagePatients(acesso) ? patientsService.get() : patientsService.getAssigned();

            const [responsePartner, responseSchedule, responsePatient] = await Promise.all([
                partnersService.getList(),
                scheduleService.getByDay(currentDay),
                patientRequest
            ]);

            this.setState({
                partnerList: filterNamedPartners(responsePartner.data || []),
                dataAgendamento: moment().format('YYYY-MM-DD'),
                datatable: responseSchedule.data || [],
                allPatientList: filterNamedPatients(responsePatient.data || []),
                currentPage: 1
            });
        } catch (error) {
            this.showError(error, 'Nao foi possivel carregar a agenda.');
        } finally {
            this.setState({ loading: false });
        }
    };

    refreshDay = async (dataAgendamento, showLoading = true) => {
        try {
            if (showLoading) {
                this.setState({ loading: true });
            }

            const responseSchedule = await scheduleService.getByDay(dataAgendamento);
            this.setState({
                datatable: responseSchedule.data || [],
                currentPage: 1
            });
        } finally {
            if (showLoading) {
                this.setState({ loading: false });
            }
        }
    };

    cancelEdit = () => {
        this.setState({
            modalShow: false,
            entrada: '',
            saida: '',
            idParceiro: '',
            idPaciente: '',
            patientList: [],
            id: '',
            saving: false,
            loadingPatients: false
        });
    };

    validateFields = () => {
        if (!this.state.entrada) {
            this.flashMessage('Horario de entrada vazio.');
            return false;
        }

        if (!this.state.idParceiro) {
            this.flashMessage('Profissional nao preenchido.');
            return false;
        }

        if (!this.state.idPaciente) {
            this.flashMessage('Paciente nao preenchido.');
            return false;
        }

        return true;
    };

    delete = async (id) => {
        try {
            this.setState({ saving: true });
            await scheduleService.delete(id);
            await this.refreshDay(moment(this.state.dataAgendamento).format('YYYYMMDD'));
            this.flashMessage('Agendamento excluido com sucesso.');
            this.setState({ modalShow: false });
        } catch (error) {
            this.showError(error, 'Nao foi possivel excluir o agendamento.');
        } finally {
            this.setState({ saving: false });
        }
    };

    saveOrUpdate = async () => {
        try {
            if (!this.validateFields()) {
                return;
            }

            const { idParceiro, idPaciente, entrada, saida, dataAgendamento, id } = this.state;
            const dataAg = moment(dataAgendamento).format('YYYYMMDD');
            const body = { dataAgendamento: dataAg, idParceiro, idPaciente, entrada, saida };

            this.setState({ saving: true });

            if (id) {
                await scheduleService.patch(body, id);
            } else {
                await scheduleService.post(body);
            }

            await this.refreshDay(dataAg);
            this.flashMessage(`Agendamento ${id ? 'atualizado' : 'inserido'} com sucesso.`);
            this.cancelEdit();
        } catch (error) {
            this.showError(error, 'Nao foi possivel salvar o agendamento.');
            this.setState({ saving: false });
        }
    };

    loadPatients = async (idParceiro, selectedPatientId = '') => {
        try {
            if (!idParceiro) {
                this.setState({ patientList: [], idPaciente: '', loadingPatients: false });
                return;
            }

            this.setState({ loadingPatients: true });
            const patientList = filterNamedPatients((await patientsService.getByGroup(idParceiro)).data || []);
            const selectedPatient = patientList.find((item) => String(item.idPacientes) === String(selectedPatientId));

            this.setState({
                patientList,
                idPaciente: selectedPatient ? String(selectedPatient.idPacientes) : '',
                loadingPatients: false
            });
        } catch (error) {
            this.setState({ patientList: [], idPaciente: '', loadingPatients: false });
            this.showError(error, 'Nao foram encontrados pacientes vinculados.');
        }
    };

    returnPartnerName = (id) => {
        const response = this.state.partnerList.find((item) => Number(item.idUsuario) === Number(id));
        return response ? `${response.Nome} ${response.Sobrenome}`.trim() : 'Nao identificado';
    };

    returnPatientName = (id) => {
        const response = this.state.allPatientList.find((item) => Number(item.idPacientes) === Number(id));
        return response ? response.nome : 'Nao identificado';
    };

    editModal = async (idAgendamento) => {
        try {
            if (idAgendamento) {
                this.setState({ loadingPatients: true });
                const { data } = await scheduleService.getById(idAgendamento);
                const item = data && data[0];

                if (!item) {
                    this.setState({ loadingPatients: false });
                    this.flashMessage('Agendamento nao encontrado.');
                    return;
                }

                this.setState({
                    ...item,
                    id: idAgendamento,
                    modalShow: true
                });
                await this.loadPatients(item.idParceiro, item.idPaciente);
                return;
            }

            this.setState({
                id: '',
                idPaciente: '',
                idParceiro: '',
                entrada: '',
                saida: '',
                patientList: [],
                modalShow: true,
                loadingPatients: false
            });
        } catch (error) {
            this.setState({ loadingPatients: false });
            this.showError(error, 'Nao foi possivel abrir o registro.');
        }
    };

    nextDay = async () => {
        try {
            const nextDate = moment(this.state.dataAgendamento).add(1, 'd');
            await this.refreshDay(nextDate.format('YYYYMMDD'));
            this.setState({ dataAgendamento: nextDate.format('YYYY-MM-DD') });
        } catch (error) {
            this.showError(error, 'Nao foi possivel carregar o proximo dia.');
        }
    };

    prevDay = async () => {
        try {
            const prevDate = moment(this.state.dataAgendamento).add(-1, 'd');
            await this.refreshDay(prevDate.format('YYYYMMDD'));
            this.setState({ dataAgendamento: prevDate.format('YYYY-MM-DD') });
        } catch (error) {
            this.showError(error, 'Nao foi possivel carregar o dia anterior.');
        }
    };

    setDay = async (dataAgendamento) => {
        try {
            await this.refreshDay(moment(dataAgendamento).format('YYYYMMDD'));
            this.setState({ dataAgendamento });
        } catch (error) {
            this.showError(error, 'Nao foi possivel carregar a data selecionada.');
        }
    };

    render() {
        const {
            datatable,
            currentPage,
            postPerPage,
            idParceiro,
            idPaciente,
            patientList,
            loading,
            saving,
            loadingPatients
        } = this.state;
        const totalPages = Math.max(1, Math.ceil(datatable.length / postPerPage));
        const safePage = Math.min(Math.max(currentPage, 1), totalPages);
        const currentData = datatable.slice((safePage - 1) * postPerPage, safePage * postPerPage);
        const completedSchedules = datatable.filter((item) => item.saida).length;
        const pendingSchedules = Math.max(datatable.length - completedSchedules, 0);

        return (
            <Container fluid className="portal-shell">
                <div className="portal-alert">
                    <Alert show={this.state.show} variant="info" dismissible onClose={() => this.setState({ show: false })}>
                        {this.state.message}
                    </Alert>
                </div>

                <div className="page-header">
                    <div>
                        <h2 className="page-title">Agenda de pacientes</h2>
                        <p className="page-subtitle">
                            {canManageSchedule(acesso)
                                ? 'O perfil Cadastro organiza os atendimentos informando profissional e paciente vinculado.'
                                : 'Consulta em leitura da agenda do dia para acompanhamento do atendimento.'}
                        </p>
                    </div>
                </div>

                <div className="portal-schedule-layout">
                    <div className="portal-schedule-summary">
                        <div className="portal-card portal-section">
                            <span className="portal-card-kicker">Resumo do dia</span>
                            <div className="portal-overview-grid">
                                <div className="portal-overview-item">
                                    <strong>{loading ? '--' : datatable.length}</strong>
                                    <span>agendamentos carregados</span>
                                </div>
                                <div className="portal-overview-item">
                                    <strong>{loading ? '--' : completedSchedules}</strong>
                                    <span>atendimentos com saida registrada</span>
                                </div>
                            </div>
                        </div>

                        <div className="portal-card portal-section">
                            <span className="portal-card-kicker">Leitura rapida</span>
                            <div className="portal-note-list">
                                <div className="portal-note">
                                    <strong>{loading ? '--' : pendingSchedules} pendentes</strong>
                                    <span>Registros ainda sem horario de saida para a data selecionada.</span>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div className="portal-card portal-section">
                        <div className="portal-card-header">
                            <div className="portal-card-copy">
                                <span className="portal-card-kicker">Agenda diaria</span>
                                <h3 className="portal-card-title">Controle de atendimentos</h3>
                                <p className="portal-card-subtitle">
                                    Navegue por data, acompanhe a ocupacao do dia e ajuste os registros quando o perfil permitir.
                                </p>
                            </div>
                            <div className="portal-table-actions">
                                <Button as={Link} variant="secondary" to="/">
                                    Voltar
                                </Button>
                                {canManageSchedule(acesso) ? (
                                    <Button variant="success" onClick={() => this.editModal()}>
                                        Adicionar
                                    </Button>
                                ) : null}
                            </div>
                        </div>

                        <div className="portal-date-nav">
                            <Button disabled={loading} variant="primary" onClick={this.prevDay}>
                                <FaArrowLeft />
                            </Button>
                            <Form.Control
                                disabled={loading}
                                value={this.state.dataAgendamento}
                                type="date"
                                onChange={item => this.setDay(item.target.value)}
                            />
                            <Button disabled={loading} variant="primary" onClick={this.nextDay}>
                                <FaArrowRight />
                            </Button>
                        </div>

                        {loading ? (
                            <PortalSpinner
                                block
                                label="Carregando agenda"
                                description="Buscando atendimentos e vinculos do dia selecionado."
                            />
                        ) : (
                            <>
                                <div className="portal-table-wrap">
                                    <div className="portal-table-viewport">
                                        <Table responsive className="portal-table">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Profissional</th>
                                                    <th>Paciente</th>
                                                    <th>Entrada</th>
                                                    <th>Saida</th>
                                                    {canManageSchedule(acesso) ? <th>Acoes</th> : null}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentData.length ? currentData.map((item) => (
                                                    <tr key={item.idAgendamento}>
                                                        <td className="portal-data-strong">{item.idAgendamento}</td>
                                                        <td>{this.returnPartnerName(item.idParceiro)}</td>
                                                        <td>{this.returnPatientName(item.idPaciente)}</td>
                                                        <td>{item.entrada || '--:--'}</td>
                                                        <td>{item.saida || '--:--'}</td>
                                                        {canManageSchedule(acesso) ? (
                                                            <td>
                                                                <div className="portal-actions">
                                                                    <Button disabled={saving} variant="link" onClick={() => this.editModal(item.idAgendamento)}>
                                                                        <FaUserEdit />
                                                                    </Button>
                                                                    <Button disabled={saving} variant="link" onClick={() => this.delete(item.idAgendamento)}>
                                                                        <FaTrashAlt />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        ) : null}
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan={canManageSchedule(acesso) ? 6 : 5}>
                                                            <div className="portal-empty-state">Nenhum agendamento encontrado para esta data.</div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>

                                <div className="portal-pagination">
                                    <PortalPagination
                                        currentPage={safePage}
                                        onPageChange={(pageNumber) => this.setState({ currentPage: pageNumber })}
                                        totalPages={totalPages}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {canManageSchedule(acesso) ? (
                    <Modal className="portal-modal" show={this.state.modalShow} onHide={this.cancelEdit}>
                        <Modal.Header closeButton>
                            <Modal.Title>Registro de agenda</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="portal-form-grid">
                                <Form.Group>
                                    <Form.Label>Profissional</Form.Label>
                                    <Form.Select
                                        disabled={saving}
                                        value={idParceiro}
                                        onChange={async (event) => {
                                            const selectedId = event.target.value;
                                            this.setState({ idParceiro: selectedId, idPaciente: '' });
                                            await this.loadPatients(selectedId);
                                        }}
                                    >
                                        <option value="">Selecione</option>
                                        {this.state.partnerList.map((partner) => (
                                            <option key={partner.idUsuario} value={partner.idUsuario}>
                                                {`${partner.Nome} ${partner.Sobrenome}`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                {loadingPatients ? (
                                    <PortalSpinner
                                        compact
                                        label="Carregando pacientes"
                                        description="Buscando a carteira do profissional selecionado."
                                    />
                                ) : null}

                                <Form.Group>
                                    <Form.Label>Paciente</Form.Label>
                                    <Form.Select
                                        disabled={saving || loadingPatients || !idParceiro}
                                        value={idPaciente}
                                        onChange={(event) => this.setState({ idPaciente: event.target.value })}
                                    >
                                        <option value="">{idParceiro ? 'Selecione' : 'Escolha primeiro o profissional'}</option>
                                        {patientList.map((patient) => (
                                            <option key={patient.idPacientes} value={patient.idPacientes}>
                                                {patient.nome}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <div className="portal-form-grid two-columns">
                                    <Form.Group>
                                        <Form.Label>Entrada</Form.Label>
                                        <Form.Control
                                            disabled={saving}
                                            value={this.state.entrada}
                                            type="time"
                                            onChange={item => this.setState({ entrada: item.target.value })}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label>Saida</Form.Label>
                                        <Form.Control
                                            disabled={saving}
                                            value={this.state.saida}
                                            type="time"
                                            onChange={item => this.setState({ saida: item.target.value })}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button disabled={saving} variant="secondary" onClick={this.cancelEdit}>
                                Fechar
                            </Button>
                            <Button disabled={saving || loadingPatients} variant="success" onClick={this.saveOrUpdate}>
                                {saving ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                ) : null}
            </Container>
        );
    }
}

export default Schedule;
